import { useEffect, createContext, useContext, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import Lenis from 'lenis';

const LenisContext = createContext(null);

export const useLenis = () => useContext(LenisContext);

const SmoothScroll = ({ children }) => {
  const lenisRef = useRef(null);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.8,
      touchMultiplier: 1.5,
      infinite: false,
      autoResize: true,
    });

    lenisRef.current = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Handle scroll on route change
  useEffect(() => {
    const shouldScrollToPortfolio = sessionStorage.getItem('returnToPortfolio') === 'true';
    
    if (pathname === '/' && shouldScrollToPortfolio) {
      // Clear the flag immediately
      sessionStorage.removeItem('returnToPortfolio');
      
      // Wait for page to fully render, then scroll to portfolio
      const scrollToPortfolio = () => {
        const portfolioSection = document.getElementById('portfolio');
        if (portfolioSection) {
          const targetY = portfolioSection.offsetTop;
          
          // Set native scroll first
          window.scrollTo(0, targetY);
          
          // Then sync Lenis
          if (lenisRef.current) {
            lenisRef.current.scrollTo(targetY, { immediate: true });
          }
        }
      };
      
      // Use multiple timeouts to ensure it works
      setTimeout(scrollToPortfolio, 0);
      setTimeout(scrollToPortfolio, 100);
    } else if (navigationType === 'PUSH') {
      // Forward navigation - scroll to top
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
    }
  }, [pathname, navigationType]);

  return (
    <LenisContext.Provider value={lenisRef}>
      {children}
    </LenisContext.Provider>
  );
};

export default SmoothScroll;
